import { useState, useMemo } from 'react';
import type { Row } from '@tanstack/react-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Check, Plus, X } from 'lucide-react';
import {
  EditableTable,
  EditableTableRowActions,
  EditableTableCell,
  type EditableTableRowActionState,
  type EditableTableAddRowActionState,
  editableRowClass,
} from '@/components/ui/data-table/editable-table';
import AssignmentCardResourceDialog from '../AssignmentCardResourceDialog';
import AssignmentCardResourceAttachmentDialog from '../AssignmentCardResourceAttachmentDialog';
import {
  type PlannedResource,
  type PlannedResourceInput,
  useUpdatePlannedResource,
  useUpdateWorkAssignmentListAttachment,
  keys as walaKeys,
} from 'components/IAP/hooks/assignmentListHooks';
import type { AssignmentListRowData } from './types';
import { NULL_VALUE } from 'constants/displayConstants';
import { Controller, useForm } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useQueryClient } from '@tanstack/react-query';
import {
  useRosterResourceOptions,
  type RosterResourceOption,
} from 'components/IAP/hooks/incidentRosterResources/incidentRosterResources';
import { useGetOrCreatePositionResource } from 'components/IAP/hooks/resourceInIncidentHooks';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'react-toastify';
import { parseError } from '@/hooks/shared/error';
import { resourcesInIncidentKeys } from 'components/IAP/hooks/resourceInIncidentHooks';

// resources are frozen while the assignment is pending review
function canModifyPlannedResources(
  row: Row<AssignmentListRowData>,
  canEditCards: boolean
) {
  return (
    canEditCards &&
    row.original.walaId !== null &&
    row.original.actualStatus !== 'awaiting_review' &&
    row.original.actualStatus !== null
  );
}

const toggleSetValue = <T,>(set: Set<T>, value: T) => {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
};

export function CardResources({
  row,
  canEditCards,
  periodNumber,
}: {
  row: Row<AssignmentListRowData>;
  canEditCards: boolean;
  periodNumber: number;
}) {
  const plannedResources = row.original.plannedResources ?? [];
  const updateWala = useUpdateWorkAssignmentListAttachment();
  const canModifyResources = canModifyPlannedResources(row, canEditCards);
  const [attachmentResource, setAttachmentResource] =
    useState<PlannedResource | null>(null);
  const [showCreateResourceRow, setShowCreateResourceRow] = useState(false);

  const handleDelete = async (resource: PlannedResource) => {
    if (!row.original.walaId) return;
    await updateWala.mutateAsync({
      walaId: row.original.walaId,
      plannedResources: (row.original.plannedResources ?? []).filter(
        (currentResource) => currentResource.id !== resource.id
      ),
    });
  };

  const handleCreateResource = async (
    values: CreatePlannedResourceFormValues
  ) => {
    if (!row.original.walaId) return;
    const newResource: PlannedResourceInput = {
      ...values,
      incidentResourceId: null,
      isDocumentation: true,
      numberOfPersons: values.numberOfPersons ?? 0,
    };
    await updateWala.mutateAsync({
      walaId: row.original.walaId,
      plannedResources: [...(row.original.plannedResources ?? []), newResource],
    });
  };

  return (
    <Card className="tw:bg-transparent" size="sm">
      <CardHeader>
        <CardTitle>5. Resources Assigned</CardTitle>
      </CardHeader>
      <CardContent>
        <AssignmentResourceMenu
          row={row}
          canEditCards={canEditCards}
          onAddDocumentationEntry={() => setShowCreateResourceRow(true)}
        />
        <EditableTable<PlannedResource>
          data={plannedResources}
          columns={[
            { id: 'resource', header: 'Resource' },
            { id: 'leader', header: 'Leader' },
            { id: 'numberOfPersons', header: '# of Persons' },
            { id: 'contactInfo', header: 'Contact Info' },
            { id: 'reportingInfo', header: 'Reporting Info / Notes' },
            { id: '204a', header: '204a' },
            { id: 'actions', header: '' },
          ]}
          getRowId={(resource) => resource.id}
          canEdit={canModifyResources}
          emptyMessage="No resources assigned"
          deleteTitle="Delete resource?"
          deleteDescription="This resource will be removed from the assignment list."
          renderRow={(resource, actions) => (
            <EditablePlannedResourceRow
              resource={resource}
              canEditCards={canModifyResources}
              actions={actions}
              onShowAttachment={() => setAttachmentResource(resource)}
            />
          )}
          renderCreateRow={(actions) => (
            <CreatePlannedResourceRow
              actions={actions}
              onCreate={handleCreateResource}
            />
          )}
          showCreateRow={showCreateResourceRow}
          onCreateRowClose={() => setShowCreateResourceRow(false)}
          onConfirmDelete={handleDelete}
          onConfirmAdd={() => {}}
        />
      </CardContent>
      {attachmentResource && (
        <AssignmentCardResourceAttachmentDialog
          onClose={() => setAttachmentResource(null)}
          data={{ ...attachmentResource, walaId: row.original.walaId }}
          periodNumber={periodNumber}
        />
      )}
    </Card>
  );
}

function AssignmentResourceMenu({
  row,
  canEditCards,
  onAddDocumentationEntry,
}: {
  row: Row<AssignmentListRowData>;
  canEditCards: boolean;
  onAddDocumentationEntry: () => void;
}) {
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showRosterModal, setShowRosterModal] = useState(false);
  const canAttachResources = canModifyPlannedResources(row, canEditCards);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="outline"
              size="sm"
              disabled={!canAttachResources}
            />
          }
        >
          <Plus /> Add
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            disabled={!canAttachResources}
            onClick={() => setShowResourceModal(true)}
          >
            + Add resource
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!canAttachResources}
            onClick={() => setShowRosterModal(true)}
          >
            + Add from roster
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!canAttachResources}
            onClick={onAddDocumentationEntry}
          >
            + Add documentation entry
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {showResourceModal && row.original.walaId && (
        <AssignmentCardResourceDialog
          walaId={row.original.walaId}
          onClose={() => setShowResourceModal(false)}
          plannedResources={row.original.plannedResources}
        />
      )}
      {showRosterModal && row.original.walaId && (
        <AssignmentCardRosterDialog
          walaId={row.original.walaId}
          plannedResources={row.original.plannedResources}
          onClose={() => setShowRosterModal(false)}
        />
      )}
    </>
  );
}

type PlannedResourceFormValues = Pick<
  PlannedResource,
  | 'name'
  | 'leader'
  | 'numberOfPersons'
  | 'contactInfo'
  | 'reportingInfo'
  | 'has204a'
>;

function EditablePlannedResourceRow({
  resource,
  canEditCards,
  actions,
  onShowAttachment,
}: {
  resource: PlannedResource;
  canEditCards: boolean;
  actions: EditableTableRowActionState;
  onShowAttachment: () => void;
}) {
  const { isEditing } = actions;
  const updateResource = useUpdatePlannedResource(resource.id);
  const defaultValues: PlannedResourceFormValues = {
    name: resource.name ?? '',
    leader: resource.leader ?? '',
    numberOfPersons: resource.numberOfPersons ?? null,
    contactInfo: resource.contactInfo ?? '',
    reportingInfo: resource.reportingInfo ?? '',
    has204a: resource.has204a ?? false,
  };
  const { register, handleSubmit, reset, control } =
    useForm<PlannedResourceFormValues>({
      defaultValues,
    });

  const handleSave = handleSubmit(async (values) => {
    await updateResource.mutateAsync({ ...resource, ...values });
    actions.finishEdit();
  });

  return (
    <TableRow
      onClick={isEditing ? undefined : actions.startEdit}
      className={editableRowClass(canEditCards, isEditing)}
    >
      <EditableTableCell
        isEditing={isEditing && resource.isDocumentation === true}
        displayValue={resource.name || NULL_VALUE}
      >
        <Input {...register('name')} />
      </EditableTableCell>
      <EditableTableCell
        isEditing={isEditing}
        displayValue={resource.leader || NULL_VALUE}
      >
        <Input {...register('leader')} />
      </EditableTableCell>
      <EditableTableCell
        isEditing={isEditing}
        displayValue={resource.numberOfPersons ?? NULL_VALUE}
      >
        <Input
          type="number"
          min={0}
          {...register('numberOfPersons', {
            setValueAs: (v) => (v === '' ? null : Number(v)),
          })}
        />
      </EditableTableCell>
      <EditableTableCell
        isEditing={isEditing}
        displayValue={resource.contactInfo || NULL_VALUE}
      >
        <Input {...register('contactInfo')} />
      </EditableTableCell>
      <EditableTableCell
        isEditing={isEditing}
        displayValue={resource.reportingInfo || NULL_VALUE}
      >
        <Input {...register('reportingInfo')} />
      </EditableTableCell>
      <EditableTableCell
        isEditing={isEditing}
        displayValue={<Checkbox checked={resource.has204a ?? false} disabled />}
      >
        <Controller
          control={control}
          name="has204a"
          render={({ field }) => (
            <Checkbox
              checked={field.value ?? false}
              onCheckedChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
      </EditableTableCell>
      <TableCell>
        <div className="tw:flex tw:items-center tw:gap-2">
          <EditableTableRowActions
            isEditing={isEditing}
            canEdit={canEditCards}
            onSave={handleSave}
            onCancel={() => {
              reset(defaultValues);
              actions.cancelEdit();
            }}
            onRequestDelete={actions.requestDelete}
          />
          {resource.has204a && !isEditing && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                onShowAttachment();
              }}
            >
              204a
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

type CreatePlannedResourceFormValues = Pick<
  PlannedResource,
  'name' | 'leader' | 'contactInfo' | 'reportingInfo' | 'has204a'
> & { numberOfPersons: number | null };

function CreatePlannedResourceRow({
  actions,
  onCreate,
}: {
  actions: EditableTableAddRowActionState;
  onCreate: (values: CreatePlannedResourceFormValues) => Promise<void>;
}) {
  const defaultValues: CreatePlannedResourceFormValues = {
    name: '',
    leader: '',
    numberOfPersons: null,
    contactInfo: '',
    reportingInfo: '',
    has204a: false,
  };
  const { register, handleSubmit, reset, control, formState } =
    useForm<CreatePlannedResourceFormValues>({ defaultValues });

  const handleSave = handleSubmit(async (values) => {
    await onCreate(values);
    await actions.finishAdd();
    reset(defaultValues);
  });

  const handleCancel = () => {
    reset(defaultValues);
    actions.cancelAdd();
  };

  return (
    <TableRow>
      <TableCell>
        <Input placeholder="Resource name" {...register('name')} />
      </TableCell>
      <TableCell>
        <Input {...register('leader')} />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          min={0}
          {...register('numberOfPersons', {
            setValueAs: (v) => (v === '' ? null : Number(v)),
          })}
        />
      </TableCell>
      <TableCell>
        <Input {...register('contactInfo')} />
      </TableCell>
      <TableCell>
        <Input {...register('reportingInfo')} />
      </TableCell>
      <TableCell>
        <Controller
          control={control}
          name="has204a"
          render={({ field }) => (
            <Checkbox
              checked={field.value ?? false}
              onCheckedChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
      </TableCell>
      <TableCell>
        <div className="tw:flex tw:gap-2">
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Save new resource"
            disabled={formState.isSubmitting}
            onClick={(event) => {
              event.stopPropagation();
              void handleSave();
            }}
          >
            <Check />
          </Button>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Cancel new resource"
            disabled={formState.isSubmitting}
            onClick={(event) => {
              event.stopPropagation();
              handleCancel();
            }}
          >
            <X />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function AssignmentCardRosterDialog({
  walaId,
  plannedResources,
  onClose,
}: {
  walaId: string;
  plannedResources: PlannedResource[] | null;
  onClose: () => void;
}) {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  const { data: options = [] } = useRosterResourceOptions();
  const updateWala = useUpdateWorkAssignmentListAttachment();
  const getOrCreatePositionResource = useGetOrCreatePositionResource();
  const [searchText, setSearchText] = useState('');
  const [selectedResourceIds] = useState<Set<string>>(
    () =>
      new Set(
        (plannedResources ?? [])
          .filter((resource) => !resource.positionId)
          .map((resource) => resource.id)
      )
  );
  const [selectedPositionIds, setSelectedPositionIds] = useState<Set<string>>(
    () =>
      new Set(
        (plannedResources ?? []).flatMap((resource) =>
          resource.positionId ? [resource.positionId] : []
        )
      )
  );

  const filteredOptions = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) =>
      [option.label, option.positionName, option.detail]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(query))
    );
  }, [options, searchText]);

  const toggleOption = (option: RosterResourceOption) => {
    if (option.kind === 'position') {
      const id = option.positionId;
      setSelectedPositionIds((previous) => toggleSetValue(previous, id));
    } else if (option.kind === 'personnel') {
      const shouldSelect = option.positionIds.some(
        (positionId) => !selectedPositionIds.has(positionId)
      );
      setSelectedPositionIds((previous) => {
        const next = new Set(previous);
        option.positionIds.forEach((positionId) => {
          if (shouldSelect) next.add(positionId);
          else next.delete(positionId);
        });
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    if (!incidentId || updateWala.isPending) return;

    try {
      const selectedPositionIdList = Array.from(selectedPositionIds);
      const existingPositionIds = new Set(
        (plannedResources ?? []).flatMap((resource) =>
          resource.positionId ? [resource.positionId] : []
        )
      );
      const positionIdsToCreate = selectedPositionIdList.filter(
        (positionId) => !existingPositionIds.has(positionId)
      );
      const createdResources = await Promise.all(
        positionIdsToCreate.map((positionId) =>
          getOrCreatePositionResource.mutateAsync({ positionId, walaId })
        )
      );

      const existingById = new Map(
        (plannedResources ?? []).map((resource) => [resource.id, resource])
      );
      const nextResourcesById = new Map<string, PlannedResourceInput>();
      Array.from(selectedResourceIds).forEach((id) => {
        const resource = existingById.get(id);
        nextResourcesById.set(id, resource ?? ({ id } as PlannedResource));
      });
      (plannedResources ?? [])
        .filter(
          (resource) =>
            resource.positionId && selectedPositionIds.has(resource.positionId)
        )
        .forEach((resource) => nextResourcesById.set(resource.id, resource));
      createdResources.forEach((resource, index) =>
        nextResourcesById.set(resource.id, {
          incidentResourceId: resource.id,
          positionId: positionIdsToCreate[index],
        } as PlannedResourceInput)
      );
      const nextResources = Array.from(nextResourcesById.values());

      await updateWala.mutateAsync({ walaId, plannedResources: nextResources });
      // prettier-ignore
      queryClient.invalidateQueries({queryKey: resourcesInIncidentKeys.byIncidentId(incidentId) });
      // prettier-ignore
      queryClient.invalidateQueries({ queryKey: walaKeys.byIncidentId(incidentId) });
      onClose();
    } catch (error) {
      toast.error(parseError(error));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="tw:w-3/4! tw:max-w-3/4!">
        <DialogHeader>
          <DialogTitle>Add From Roster</DialogTitle>
        </DialogHeader>
        <Input
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search by name, position, or contact"
          aria-label="Search roster"
        />
        <p className="tw:text-muted-foreground tw:italic">
          Pick a person to plan whoever holds a seat today, or pick the position
          itself to plan the seat even while it is unassigned.
        </p>
        <div className="tw:max-h-[55vh] tw:overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="tw:w-12">Select</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Contact / Detail</TableHead>
                <TableHead>Type</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOptions.map((option) => (
                <TableRow key={option.key}>
                  <TableCell>
                    <Checkbox
                      checked={
                        option.kind === 'position'
                          ? selectedPositionIds.has(option.positionId)
                          : option.kind === 'personnel'
                            ? option.positionIds.length > 0 &&
                              option.positionIds.every((positionId) =>
                                selectedPositionIds.has(positionId)
                              )
                            : false
                      }
                      onCheckedChange={() => toggleOption(option)}
                      aria-label={`Select ${option.label}`}
                    />
                  </TableCell>
                  <TableCell>{option.label ?? NULL_VALUE}</TableCell>
                  <TableCell>{option.positionName ?? NULL_VALUE}</TableCell>
                  <TableCell>{option.detail ?? NULL_VALUE}</TableCell>
                  <TableCell>{option.kind}</TableCell>
                </TableRow>
              ))}
              {!filteredOptions.length && (
                <TableRow>
                  <TableCell colSpan={5}>No roster entries found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={updateWala.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={updateWala.isPending}>
            {updateWala.isPending ? 'Adding...' : 'Add Selected'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
